import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

const DFY_PRICE = 499;
const SELF_SERVE_PRICE = 299;
const CREDIT_IF_UPGRADE = 200;

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function PilotPage() {
  const query = useQuery();
  const variant = (query.get('variant') || 'dfy').toLowerCase();

  const startLink = variant === 'self-serve' ? '/trial?plan=starter' : '/trial?plan=growth';
  const startLabel = variant === 'self-serve' ? `Start Self-Serve Pilot — $${SELF_SERVE_PRICE}` : `Start DFY Pilot — $${DFY_PRICE}`;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* HERO */}
      <header className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-full px-4 py-2 text-sm text-indigo-800 mb-5">
          <span className="font-semibold">📊 Pilot snapshot:</span>
          28% fewer order-status tickets • +6% add-to-cart CTR • 48h setup
        </div>

        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
          Reduce support tickets with a product-aware AI assistant.
        </h1>

        <p className="text-gray-700 max-w-3xl mx-auto text-lg mb-6">
          We set up your workspace, ingest your content, and deliver a private preview in about 48 hours.
          Your trial then runs for 7 days so you can test it with real questions before deciding on the next step.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={startLink}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 font-medium"
          >
            {startLabel}
          </Link>
          <Link
            to="/demo/rag?mode=dtc_supplements"
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-50 font-medium"
          >
            View live demo
          </Link>
          <a
            href="/install.html"
            className="px-6 py-3 text-indigo-600 hover:underline font-medium"
          >
            Install guide
          </a>
        </div>

        <p className="mt-4 text-xs text-gray-500">
          Upgrade within 30 days and receive a <strong>${CREDIT_IF_UPGRADE}</strong> credit toward Pro.
        </p>
      </header>

      {/* WHAT YOU GET */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-5 text-center">What the pilot includes</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-5 border rounded-xl bg-white shadow-sm">
            <h3 className="font-semibold mb-2">Order lookup first</h3>
            <p className="text-sm text-gray-700">
              Customers can check order status, tracking, and items without opening a ticket.
              This is the fastest way to reduce support load.
            </p>
          </div>

          <div className="p-5 border rounded-xl bg-white shadow-sm">
            <h3 className="font-semibold mb-2">Grounded product answers</h3>
            <p className="text-sm text-gray-700">
              We tune the assistant to answer from your actual content — policies, pages, FAQs, and product data.
            </p>
          </div>

          <div className="p-5 border rounded-xl bg-white shadow-sm">
            <h3 className="font-semibold mb-2">FAQ quick-match layer</h3>
            <p className="text-sm text-gray-700">
              Common questions are answered quickly without unnecessary model calls, keeping the experience fast and cost-effective.
            </p>
          </div>

          <div className="p-5 border rounded-xl bg-white shadow-sm">
            <h3 className="font-semibold mb-2">Escalation hand-off</h3>
            <p className="text-sm text-gray-700">
              When something needs a human, we pass the conversation plus a suggested reply to Slack, email, or your helpdesk.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mb-12 bg-indigo-50 border-l-4 border-indigo-600 p-6 rounded-xl">
        <h2 className="text-2xl font-semibold mb-3">How the pilot works</h2>
        <ol className="list-decimal pl-5 text-gray-700 space-y-2 text-sm">
          <li><strong>Sign up:</strong> choose DFY or self-serve and confirm your card.</li>
          <li><strong>We build:</strong> collect content, create the tenant, curate the dataset, and test responses.</li>
          <li><strong>Preview is delivered:</strong> your private demo becomes available once setup is complete, usually within 48 hours.</li>
          <li><strong>Trial runs for 7 days:</strong> test real questions and decide whether to install or upgrade.</li>
        </ol>
      </section>

      {/* TWO OPTIONS */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-5">Choose your pilot</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-6 border rounded-xl bg-white shadow-sm">
            <div className="text-sm text-gray-500 mb-1">Done-for-you pilot</div>
            <h3 className="text-xl font-semibold mb-2">${DFY_PRICE}</h3>
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1 mb-5">
              <li>We handle the setup and tuning</li>
              <li>Best option if you want a hands-off launch</li>
              <li>Includes the curated demo and trial</li>
              <li>Install support available</li>
            </ul>
            <Link
              to="/trial?plan=growth"
              className="block text-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Start DFY Pilot
            </Link>
          </div>

          <div className="p-6 border rounded-xl bg-white shadow-sm">
            <div className="text-sm text-gray-500 mb-1">Self-serve pilot</div>
            <h3 className="text-xl font-semibold mb-2">${SELF_SERVE_PRICE}</h3>
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1 mb-5">
              <li>Lower-cost entry point</li>
              <li>We still prepare the preview for you</li>
              <li>You handle the final install</li>
              <li>Best for fast validation</li>
            </ul>
            <Link
              to="/trial?plan=starter"
              className="block text-center px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Start Self-Serve Pilot
            </Link>
          </div>
        </div>
      </section>

      {/* MONTHLY */}
      <section className="mb-12 border rounded-xl p-6 bg-white shadow-sm">
        <h2 className="text-2xl font-semibold mb-4">Monthly plans after the pilot</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-gray-500">Starter</div>
            <div className="text-2xl font-bold mt-1">$79<span className="text-xs font-normal">/mo</span></div>
            <ul className="mt-3 text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>Self-serve setup</li>
              <li>FAQ + order lookup</li>
              <li>Basic support</li>
            </ul>
          </div>

          <div className="p-4 border rounded-lg bg-indigo-50">
            <div className="text-sm text-gray-500">Growth</div>
            <div className="text-2xl font-bold mt-1">$299<span className="text-xs font-normal">/mo</span></div>
            <ul className="mt-3 text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>DFY onboarding</li>
              <li>Escalation webhook</li>
              <li>Monthly tuning</li>
            </ul>
          </div>

          <div className="p-4 border rounded-lg">
            <div className="text-sm text-gray-500">Pro</div>
            <div className="text-2xl font-bold mt-1">$999<span className="text-xs font-normal">/mo</span></div>
            <ul className="mt-3 text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>Full catalog ingestion</li>
              <li>Advanced connectors</li>
              <li>Priority support</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="text-center">
        <h3 className="text-xl font-semibold mb-2">Ready to try it on your store?</h3>
        <p className="text-sm text-gray-700 mb-5">
          Pick a pilot, we prepare the workspace, and you get a private preview once setup is complete.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/trial?plan=growth"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Start DFY Pilot
          </Link>
          <Link
            to="/trial?plan=starter"
            className="px-6 py-3 border rounded-lg hover:bg-gray-50"
          >
            Start Self-Serve Pilot
          </Link>
          <Link
            to="/demo/rag?mode=dtc_supplements"
            className="px-6 py-3 text-indigo-600 hover:underline"
          >
            Preview demo first
          </Link>
        </div>
      </section>
    </div>
  );
}
